import { useState, type ReactNode } from "react";
import { Pencil, RefreshCw } from "lucide-react";
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  ItemCell,
  LoadingState,
  MarginBar,
  Sparkline,
  TableEmpty,
  Tag,
  TileGrid,
  useToast,
  type VizTone,
} from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { intlLocale } from "@/lib/formatting/locale";
import { formatDateTime } from "@/lib/formatting/date-time";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import { usePaidServicesUsageQuery } from "../services/usePaidServicesUsageQuery";
import { useAppConfigsQuery } from "@/shared/app-configs/services/useAppConfigsQuery";
import { AppConfigDetails } from "@/shared/app-configs/components/AppConfigDetails";
import { effectiveValue } from "@/shared/app-configs/lib/configValue";
import type {
  PaidServiceLimit,
  PaidServiceUsage,
} from "../types/paid-services.types";

const copy = keys.paidServices;

type Props = { locale: string };

/** At or past this share of the cap the meter turns amber, at the cap red. */
const WARN_PERCENT = 80;

const cardTitle = { margin: 0, fontSize: 15, fontWeight: 600 } as const;
const cardNote = {
  margin: "2px 0 0",
  fontSize: 13,
  color: "var(--rd-text-secondary)",
} as const;

function meterTone(row: PaidServiceUsage): VizTone {
  if (row.capReachedAt || (row.percentOfCap ?? 0) >= 100) return "bad";
  if ((row.percentOfCap ?? 0) >= WARN_PERCENT) return "warn";
  return "blue";
}

/** A card's opening: the 15/600 title, its one-line note, an optional action. */
function CardHead({
  title,
  note,
  action,
}: {
  title: string;
  note?: string;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={cardTitle}>{title}</h2>
        {note ? <p style={cardNote}>{note}</p> : null}
      </div>
      {action ?? null}
    </div>
  );
}

export function PaidServicesPage({ locale }: Props) {
  const t = useTranslations();
  const query = usePaidServicesUsageQuery();
  // The caps and limits are app configuration keys: each opens the same edit
  // drawer as App configuration, here, so an admin never leaves the page.
  const configs = useAppConfigsQuery();
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const toast = useToast();
  /** Re-reads the usage; a quick result that changes no data, so a neutral toast. */
  const refresh = async () => {
    const result = await query.refetch();
    if (!result.isError) toast.show({ tone: "neutral", icon: RefreshCw, message: t("paidServices.toastRefreshed") });
  };
  const number = new Intl.NumberFormat(intlLocale(locale));
  const percent = new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: 1,
  });
  const monthName = (month: string) => {
    const [year, index] = month.split("-").map(Number);
    return new Intl.DateTimeFormat(intlLocale(locale), {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(year, index - 1, 1)));
  };
  const nameOf = (row: PaidServiceUsage) =>
    locale === "ar" ? row.nameAr : row.nameEn;

  const page = t(copy.nav).toLocaleLowerCase(intlLocale(locale));
  if (query.isLoading) {
    return (
      <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} rows={4} />
    );
  }
  if (query.error || !query.data) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void query.refetch()}
        retryLabel={query.isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={query.isRefetching}
      />
    );
  }

  const services = query.data.services;
  const history = services[0]?.months ?? [];
  const limits = query.data.limits ?? [];
  const topStores = query.data.topRoutesStores ?? [];
  const limitName = (row: PaidServiceLimit) =>
    locale === "ar" ? row.nameAr : row.nameEn;
  const configFor = (key: string) => configs.data?.find((config) => config.key === key) ?? null;
  // A saved value shows at once, from the configs the drawer just refreshed;
  // the usage figures catch up when the drawer closes.
  const liveNumber = (key: string, fallback: number) => {
    const config = configFor(key);
    if (!config) return fallback;
    const value = Number(effectiveValue(config));
    return Number.isFinite(value) ? value : fallback;
  };
  const openEdit = (key: string) => {
    if (!configFor(key)) return;
    setEditKey(key);
    setEditOpen(true);
  };
  const editButton = (key: string, name: string) =>
    configFor(key) ? (
      <Button
        variant="ghost"
        size="sm"
        icon={Pencil}
        aria-label={t("paidServices.editNamed", { name })}
        onClick={() => openEdit(key)}
      >
        {t("paidServices.edit")}
      </Button>
    ) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeaderActions>
        <Button
          variant="secondary"
          size="sm"
          icon={RefreshCw}
          loading={query.isFetching}
          loadingLabel={t("paidServices.refreshing")}
          onClick={() => void refresh()}
        >
          {t(copy.refresh)}
        </Button>
      </PageHeaderActions>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 820 }}>
        <p style={{ margin: 0, fontSize: 14, color: "var(--rd-text-secondary)" }}>
          {t(copy.subtitle)}
        </p>
        <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-tertiary)" }}>
          {t(copy.countingNote)}
        </p>
      </div>

      <section
        aria-label={t(copy.thisMonthLabel)}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <CardHead
          title={t(copy.thisMonth, {
            month: monthName(services[0]?.currentMonth ?? ""),
          })}
        />
        <TileGrid min={320}>
          {services.map((row) => {
            const months = [...row.months].reverse();
            return (
              <Card
                key={row.service}
                padding={16}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ ...cardTitle, margin: 0 }}>
                      <bdi>{nameOf(row)}</bdi>
                    </h3>
                    <p className="rd-cell-sub" dir="ltr" style={{ margin: "2px 0 0", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {row.capKey}
                    </p>
                  </div>
                  {row.capReachedAt ? (
                    <Tag tone="red" title={formatDateTime(row.capReachedAt, locale)}>
                      {t(copy.capReached)}
                    </Tag>
                  ) : null}
                  {editButton(row.capKey, nameOf(row))}
                </div>

                <dl
                  className="rd-kv"
                  style={{ gridTemplateColumns: "auto minmax(0,1fr)", rowGap: 6, fontSize: 13 }}
                >
                  <dt>{t(copy.used)}</dt>
                  <dd className="rd-num" style={{ fontWeight: 600 }}>
                    {number.format(row.currentUnits)}
                  </dd>
                  <dt>{t(copy.cap)}</dt>
                  <dd className="rd-num">
                    {liveNumber(row.capKey, row.cap) > 0
                      ? number.format(liveNumber(row.capKey, row.cap))
                      : t(copy.unlimited)}
                  </dd>
                </dl>

                {row.cap > 0 && row.percentOfCap !== null ? (
                  <MarginBar
                    label={t(copy.percentOfCap)}
                    note={t(copy.percentOfCapLabel, { service: nameOf(row) })}
                    percent={row.percentOfCap}
                    value={`${percent.format(row.percentOfCap)}%`}
                    tone={meterTone(row)}
                  />
                ) : null}
                {row.cap > 0 ? null : (
                  <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
                    {t("paidServices.noCapNote")}
                  </span>
                )}

                {months.length > 0 ? (
                  <Sparkline
                    values={months.map((entry) => entry.units)}
                    label={t(copy.history)}
                    trend={monthName(row.currentMonth)}
                    tips={months.map(
                      (entry) => `${monthName(entry.month)}: ${number.format(entry.units)}`,
                    )}
                    ariaLabel={`${nameOf(row)}: ${t(copy.historyHint)}`}
                  />
                ) : null}
              </Card>
            );
          })}
        </TileGrid>
      </section>

      {limits.length > 0 ? (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CardHead title={t(copy.limits)} note={t(copy.limitsHint)} />
          <DataTable
            label={t(copy.limits)}
            rows={limits}
            rowKey={(row) => row.limitKey}
            minWidth={560}
            empty={null}
            onRowClick={(row) => openEdit(row.limitKey)}
            columns={[
              {
                key: "limit-name",
                label: t(copy.service),
                width: 2,
                render: (row) => (
                  <ItemCell
                    badge="route"
                    name={<bdi>{limitName(row)}</bdi>}
                    meta={<span dir="ltr">{row.limitKey}</span>}
                  />
                ),
              },
              {
                key: "period",
                label: "",
                width: 1.2,
                render: (row) => (
                  <span style={{ color: "var(--rd-text-secondary)" }}>
                    {t(row.period === "utc_day" ? copy.perStoreDay : copy.perDelivery)}
                  </span>
                ),
              },
              {
                key: "limit",
                label: t(copy.limit),
                width: "120px",
                align: "end",
                numeric: true,
                render: (row) => {
                  const limit = liveNumber(row.limitKey, row.limit);
                  return limit > 0 ? number.format(limit) : t(copy.unlimited);
                },
              },
              {
                key: "edit",
                label: "",
                width: "44px",
                align: "end",
                render: (row) =>
                  configFor(row.limitKey) ? (
                    <Pencil aria-hidden="true" size={14} style={{ color: "var(--rd-text-icon)" }} />
                  ) : null,
              },
            ]}
          />
        </section>
      ) : null}

      {query.data.topRoutesStores ? (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CardHead title={t(copy.topStores)} note={t(copy.topStoresHint)} />
          <DataTable
            label={t(copy.topStores)}
            rows={topStores}
            rowKey={(row) => row.storeId}
            minWidth={560}
            empty={
              <TableEmpty illustration="admin-empty-table" title={t(copy.noRoutesUsage)}>
                {t("paidServices.topStoresEmptyText")}
              </TableEmpty>
            }
            columns={[
              {
                key: "store",
                label: t(copy.store),
                width: 2,
                render: (row) => (
                  <ItemCell
                    badge="map"
                    name={<bdi>{row.storeName ?? row.storeId}</bdi>}
                    meta={row.storeName ? <span dir="ltr">{row.storeId}</span> : undefined}
                  />
                ),
              },
              {
                key: "essentials",
                label: t(copy.essentials),
                width: "110px",
                align: "end",
                numeric: true,
                render: (row) => number.format(row.essentialsUnits),
              },
              {
                key: "pro",
                label: t(copy.pro),
                width: "110px",
                align: "end",
                numeric: true,
                render: (row) => number.format(row.proUnits),
              },
              {
                key: "total",
                label: t(copy.total),
                width: "110px",
                align: "end",
                numeric: true,
                render: (row) => (
                  <span style={{ fontWeight: 600 }}>{number.format(row.units)}</span>
                ),
              },
            ]}
          />
        </section>
      ) : null}

      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <CardHead title={t(copy.history)} note={t(copy.historyHint)} />
        <DataTable
          label={t(copy.history)}
          rows={history.map((entry) => entry.month)}
          rowKey={(month) => month}
          minWidth={840}
          empty={
            <TableEmpty illustration="admin-empty-table" title={t("paidServices.historyEmptyTitle")}>
              {t("paidServices.historyEmptyText")}
            </TableEmpty>
          }
          columns={[
            {
              key: "month",
              label: t(copy.month),
              render: (month) => monthName(month),
            },
            ...services.map((service) => ({
              key: service.service,
              label: nameOf(service),
              align: "end" as const,
              numeric: true,
              render: (month: string) =>
                number.format(
                  service.months.find((entry) => entry.month === month)?.units ?? 0,
                ),
            })),
          ]}
        />
      </section>

      <AppConfigDetails
        config={editKey ? configFor(editKey) : null}
        open={editOpen && editKey != null && configFor(editKey) != null}
        onClose={() => {
          setEditOpen(false);
          // The share of each cap is worked out by the usage read.
          void query.refetch();
        }}
        sectionLabel={t(copy.nav)}
      />
    </div>
  );
}
